/**
 * Shared in-memory cache for GET /api/settings/public.
 *
 * Both `usePublicSettings` (marketing pricing/plans) and `SEOManager`
 * (dynamic SEO meta on route changes) need this endpoint. Before this
 * module they each had their own fetch, so Catchpoint saw the endpoint
 * hit ~5 times per pageload. This module gives them a single source of
 * truth with three layers:
 *
 *   1. `cachedSettings` — a resolved response cached for CACHE_DURATION.
 *   2. `inflightPromise` — the request in flight, so concurrent callers
 *      piggy-back on the same Promise instead of firing duplicates.
 *   3. On failure, all callers get `null` and are expected to fall back
 *      to their own defaults; we do not retry from here.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

let cachedSettings = null;
let cacheTime = 0;
let inflightPromise = null;

export function getCachedSettings() {
  return cachedSettings;
}

export function isCacheFresh() {
  return cachedSettings && (Date.now() - cacheTime < CACHE_DURATION);
}

export function fetchPublicSettings() {
  if (isCacheFresh()) return Promise.resolve(cachedSettings);
  if (inflightPromise) return inflightPromise;

  inflightPromise = fetch(`${API_URL}/api/settings/public`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data) {
        cachedSettings = data;
        cacheTime = Date.now();
      }
      return data;
    })
    .catch(() => null)
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
}
